import {
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    AbstractControlOptions,
    ValidatorFn,
} from '@angular/forms';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { ComponentWithForm } from '@models';

import {
    BehaviorSubject,
    forkJoin,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { FormComponent } from './ui-form.component';

export type ControlConfig<T> = Record<keyof T, any[]>;

export type FieldConfigFn<T> = (
    key: keyof T,
    keyNullCheck?: boolean,
    validators?: ValidatorFn[] | null,
) => ControlConfig<T>;

export type UpsertPageDefaultValues<T> = {
    [K in keyof T]:
    T[K] |
    {
        /**
         * Default value of FormControl
         */
        value: T[K];
        /**
         * Initial disabled state
         */
        disabled: boolean;
    }
};

export type UpsertPageValidators<T> = {
    [K in keyof T]?:
    Object | ValidatorFn | ValidatorFn[] | AbstractControlOptions | null | undefined
};

/**
 * Page Component route dependent `entities/:id`
 * used for creating, editing an entity
 */
export abstract class UpsertPageComponent<T>
    extends FormComponent<T>
    implements ComponentWithForm, OnDestroy, OnInit {
    /**
     * Dependendent on route param `id`. E.g.: `entities/:id`
     * Used to decide how form initialization in `ngOnInit` is treated
     */
    public get isEditMode() {
        return this._isEditMode;
    }

    public get hasUnsavedChanges() {
        return this._ignoreDirtyStatus ?
            false :
            !!this.form &&
            (
                this.loading$.value ||
                this.form.dirty
            );
    }

    /**
     * Defaults are a declarative helper used for Form population / hydration
     * within `hydrate` function of field FormControl available within `_hydrateForm` method
     * if in create mode or if `isEditMode` and instance does not specify value
     * E.g. : `{name: '' }`;  `hydrate('name')` // name.value === ''
     */
    public abstract get defaults(): UpsertPageDefaultValues<T>;

    /**
     * Validators used within `hydrate` function of field FormControl
     * available within `_hydrateForm` method
     */
    public abstract get validators(): UpsertPageValidators<T>;

    public get isSubmitDisabled() {
        return this._isSubmitDisabled;
    }

    /**
     * id property is extracted from route params
     * and dictates if in Create / Edit mode.
     */
    public get id() { return this._id; }
    public set id(id: number) {
        this._id = id;
        this._isEditMode = !isNaN(id);
    }

    protected get _baseUrl() {
        const [baseRoute] = this._router.url
            .split('/')
            .filter(Boolean);

        return baseRoute ? `/${baseRoute}` : '/';
    }

    public initialContextLoading$ = new BehaviorSubject(true);
    public loading$ = new BehaviorSubject(true);

    protected _initialContext$ = [of(null)] as Observable<any>[];
    protected _ignoreDirtyStatus = false;
    protected _destroyed$ = new Subject<void>();

    protected get _isSubmitDisabled() {
        return this.loading$.value ||
            !this.form ||
            !this.form.dirty ||
            !this.form.valid;
    }

    private _id = NaN;
    private _isEditMode = false;

    constructor(
        protected _route: ActivatedRoute,
        protected _router: Router,
        protected _appState: AppStateFacade,
    ) {
        super();
        this.id = Number(this._route.snapshot.params['id']);
        _appState.user.folder.change$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => this._ignoreDirtyStatus = true);
    }

    ngOnInit() {
        forkJoin(this._initialContext$)
            .pipe(
                switchMap(() =>
                    !this.isEditMode ?
                        of(undefined) :
                        this._getInstance(),
                ),
                tap(_ => this.initialContextLoading$.next(false)),
                takeUntil(this._destroyed$),
            ).subscribe(
                (formData?: T) => {
                    const configFn = this._getFieldConfigFn(formData);

                    this._initForm(
                        this._getControlsConfig(configFn),
                        configFn,
                        formData,
                    );
                },
                () => this.isEditMode && this._router.navigate([this._baseUrl]),
            );
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    protected abstract _getInstance(): Observable<T>;

    protected abstract _initForm(
        controlsConfig: ControlConfig<T>,
        getFieldConfig: FieldConfigFn<T>,
        form?: T,
    ): void;

    protected _getControlsConfig(configFn: FieldConfigFn<T>) {
        return Object.keys(this.fields)
            .reduce((controlConfig, key) => ({
                ...controlConfig,
                ...configFn(key as keyof T),
            }), {}) as ControlConfig<T>;
    }

    protected _forceNavigateBaseUrl = () => {
        // mark as pristine to prevent trigger of unsaved changes guard
        this.form!.markAsPristine();
        this._router.navigate([this._baseUrl]);
    }

    private _getFieldConfigFn = (formData?: T): FieldConfigFn<T> => {
        return (key: keyof T, ignoreNullValues = false, validators?: ValidatorFn[] | null) => {
            // if <T> contains key 'id', it will be skipped as to not pollute the FormControl.
            if (key === 'id') { return {} as ControlConfig<T>; }
            const useFormData = !!formData &&
                (
                    !ignoreNullValues ||
                    formData![key] != null
                );

            const value = useFormData ?
                this._getFieldValue(key, formData!) :
                this.defaults[key];

            return {
                [key]: [
                    value,
                    validators || this.validators[key],
                ],
            } as ControlConfig<T>;
        };
    }

    private _getFieldValue = (key: keyof T, formData: T) =>
        this._hasDisabled(key) ?
            {
                value: formData[key],
                disabled: (this.defaults[key] as any).disabled,
            } :
            formData[key]

    private _hasDisabled(key: keyof T) {
        return this.defaults[key] &&
            (this.defaults[key] as any).disabled !== undefined;
    }
}
